import { useState, useEffect, useRef } from 'react';
import { analyzeReceipt } from '../utils/analyzeReceipt';

/**
 * ReceiptAnalyzer
 * Upload → Analyze → Review → Confirm flow for receipt line items.
 *
 * Props:
 *   packageId       UUID of encomienda_packages row (informational)
 *   encomiendaId    UUID of encomienda (informational)
 *   existingItems   Items already saved for this package
 *   onItemsConfirmed(confirmedItems[]) — called when staff saves
 *   onClose()       — dismiss the panel
 */
const ReceiptAnalyzer = ({
  packageId,
  encomiendaId,
  existingItems = [],
  onItemsConfirmed,
  onClose,
  preloadedFile = null,
}) => {
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [editedItems, setEditedItems] = useState([]);
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);
  const savingRef = useRef(false);

  useEffect(() => {
    if (preloadedFile && !file && !analyzing && !result) {
      setFile(preloadedFile);
      handleAnalyze(preloadedFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadedFile]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleAnalyze = async (f) => {
    setAnalyzing(true);
    setError(null);
    try {
      const res = await analyzeReceipt(f);
      if (!res.success) {
        setError(res.error || 'Error al analizar el recibo.');
        return;
      }
      setResult(res);
      setEditedItems((res.items || []).map(item => ({
        ...item,
        quantity: Number(
          item.quantity ?? item.cantidad ?? item.qty ?? 1
        ),
        unit_price: Number(
          item.unit_price ??
          item.valor_unitario ??
          item.unit_value ??
          item.price ?? 0
        ),
        description: (
          item.description ||
          item.descripcion ||
          item.name ||
          item.product_name || ''
        ).trim(),
      })));
    } catch (err) {
      setError('Error al analizar: ' + err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    if (selected.size > 5 * 1024 * 1024) {
      setError('Archivo muy grande. Máximo 5MB.');
      return;
    }
    setFile(selected);
    setError(null);
    handleAnalyze(selected);
  };

  const updateItem = (id, field, value) => {
    setEditedItems(prev =>
      prev.map(item => item.id === id ? { ...item, [field]: value } : item)
    );
  };

  const removeItem = (id) => {
    setEditedItems(prev => prev.filter(item => item.id !== id));
  };

  const addBlankItem = () => {
    setEditedItems(prev => [
      ...prev,
      {
        id: 'manual-' + Date.now(),
        description: '',
        quantity: 1,
        unit_price: 0,
        total_price: 0,
        condition: 'NEW',
        confirmed: false,
      },
    ]);
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setEditedItems([]);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    // Synchronous guard — beats React's batched setSaving update.
    // Even a double-click within the same render cycle hits this first.
    if (savingRef.current) return;
    const confirmed = editedItems.filter(i => i.confirmed);
    if (confirmed.length === 0) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await onItemsConfirmed(confirmed);
    } catch (err) {
      console.error('[ReceiptAnalyzer] save failed:', err);
      alert('Error al guardar artículos: ' + (err?.message || 'desconocido'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const confirmedCount = editedItems.filter(i => i.confirmed).length;

  // ── Styles ────────────────────────────────────────────────────────────────
  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <style>{`@keyframes ra-spin { to { transform: rotate(360deg); } }`}</style>

      {/* Error banner */}
      {error && !analyzing && (
        <div className="badge badge-error w-full p-3 flex items-center justify-between">
          <span>{error}</span>
          {!result && (
            <button
              onClick={handleReset}
              className="btn btn-ghost"
              style={{ color: 'white', textDecoration: 'underline', padding: '2px 8px' }}
            >
              Intentar de nuevo
            </button>
          )}
        </div>
      )}

      {/* ZONE 1: Upload — shown only when no file selected yet */}
      {!file && !analyzing && !result && (
        <div 
          className="tile" 
          style={{ 
            borderStyle: 'dashed', 
            borderWidth: '2px', 
            textAlign: 'center', 
            padding: '40px 20px',
            cursor: 'pointer',
            background: 'var(--bg)'
          }} 
          onClick={() => fileInputRef.current?.click()}
        >
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5" style={{ margin: '0 auto' }}>
            <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            <circle cx="18" cy="18" r="4" fill="var(--tile)" stroke="var(--text-tertiary)" />
            <path d="M16 18h4m-2-2v4" strokeWidth="1.5" />
          </svg>
          <p className="tile-title mt-4">Subir recibo para análisis</p>
          <p className="metric-sub">JPEG · PNG · PDF — máximo 5MB</p>
          <span className="btn btn-primary mt-4" style={{ background: 'var(--blue)' }}>Seleccionar archivo</span>
          <input
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            ref={fileInputRef}
          />
        </div>
      )}

      {/* ZONE 2: Analyzing spinner */}
      {analyzing && (
        <div className="text-center py-12">
          <div style={{
            width: '32px', height: '32px',
            border: '3px solid var(--border)',
            borderTopColor: 'var(--blue)',
            borderRadius: '50%',
            animation: 'ra-spin 0.8s linear infinite',
            margin: '0 auto 16px',
          }} />
          <p className="tile-title">Analizando recibo...</p>
          <p className="metric-sub">Identificando artículos y precios</p>
        </div>
      )}

      {/* ZONE 3: Results review */}
      {result && !analyzing && (
        <div className="space-y-4">
          {/* Header: retailer + confidence + date */}
          <div className="flex justify-between items-center">
            <div>
              <span className="tile-title" style={{ fontSize: '16px' }}>{result.retailer || 'Tienda desconocida'}</span>
              {result.receipt_date && (
                <span className="metric-sub ml-3">{result.receipt_date}</span>
              )}
            </div>
            <span className={`badge ${result.confidence > 0.85 ? 'badge-done' : 'badge-hold'}`}>
              {Math.round((result.confidence || 0) * 100)}% confianza
            </span>
          </div>

          {/* Items table */}
          <div className="overflow-x-auto border rounded-lg" style={{ borderColor: 'var(--border)' }}>
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-gray-50 border-b" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
                  <th className="p-2 metric-label text-center w-12">Cant</th>
                  <th className="p-2 metric-label text-left">Descripción</th>
                  <th className="p-2 metric-label text-center w-24">V. Unit.</th>
                  <th className="p-2 metric-label text-center w-20">Total</th>
                  <th className="p-2 metric-label text-center w-10">✓</th>
                  <th className="p-2 metric-label text-center w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
                {editedItems.map(item => {
                  const qty = parseFloat(item.quantity) || 0;
                  const up = parseFloat(item.unit_price) || 0;
                  const total = qty * up;
                  return (
                    <tr key={item.id} className="hover:bg-gray-50/50" style={item.confirmed ? { background: 'var(--green-soft)' } : {}}>
                      <td className="p-2">
                        <input
                          type="number"
                          className="input p-1 text-center"
                          value={item.quantity}
                          onChange={e => updateItem(item.id, 'quantity', e.target.value)}
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="text"
                          className="input p-1"
                          value={item.description}
                          onChange={e => updateItem(item.id, 'description', e.target.value)}
                          placeholder="Descripción"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          className="input p-1 text-right"
                          value={item.unit_price}
                          onChange={e => updateItem(item.id, 'unit_price', e.target.value)}
                        />
                      </td>
                      <td className="p-2 text-right font-mono font-bold" style={{ color: 'var(--text-main)' }}>
                        ${total.toFixed(2)}
                      </td>
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!item.confirmed}
                          onChange={e => updateItem(item.id, 'confirmed', e.target.checked)}
                          style={{ width: '16px', height: '16px' }}
                        />
                      </td>
                      <td className="p-2 text-center">
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="btn-ghost"
                          style={{ padding: '2px', color: 'var(--text-tertiary)' }}
                        >×</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button type="button" onClick={addBlankItem} className="btn btn-ghost" style={{ color: 'var(--avianca-red)', fontWeight: 'bold' }}>
            + Agregar artículo
          </button>

          <p className="metric-sub">
            Revise cada artículo antes de guardar. Los precios son del recibo original.
          </p>
        </div>
      )}

      {/* Footer actions */}
      <div className="divider" />
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <button type="button" onClick={onClose} className="btn btn-secondary">
          × Cancelar
        </button>
        <div className="flex gap-2 flex-wrap">
          {result && (
            <button type="button" onClick={handleReset} className="btn btn-secondary">
              ↺ Otro recibo
            </button>
          )}
          {result && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || confirmedCount === 0}
              className="btn btn-primary"
              style={{ background: 'var(--blue)' }}
            >
              {saving
                ? 'Guardando...'
                : `✓ Guardar ${confirmedCount > 0 ? `(${confirmedCount})` : ''} artículos`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceiptAnalyzer;
